<template>
    <div class="table-btn-col name-number-table-wrap">
        <div v-if="normalizedRows.length > 0 && showExport" class="data-export-btn">
            <a class="export-btn btn btn_outline_primary buttons" href="javascript:;" @click="downloadCsv">
                Export
            </a>
        </div>

        <table v-if="normalizedRows.length > 0" class="name-number-data">
            <tr class="cart-data-col">
                <th v-if="hasName" class="data-heading">Name</th>
                <th v-if="hasNumber" class="data-heading">Number</th>
                <th class="data-heading">Size</th>
            </tr>
            <tr v-for="(row, key) in normalizedRows" :key="key" class="cart-data-col">
                <td v-if="hasName">{{ row.name }}</td>
                <td v-if="hasNumber">{{ row.number }}</td>
                <td>{{ row.size }}</td>
            </tr>
        </table>
    </div>
</template>

<script>
export default {
    name: "NameNumberTable",
    props: {
        rows: {
            type: Array,
            required: true
        },
        showExport: {
            type: Boolean,
            required: false,
            default: true
        },
        csvFileName: {
            type: String,
            required: false,
            default: "NameAndNumber.csv"
        },
        itemContext: {
            type: Object,
            required: false,
            default: null
        },
        csvHeader: {
            type: String,
            required: false,
            default: "Name,Number,Size\n"
        }
    },
    computed: {
        normalizedRows() {
            if (!this.rows || !Array.isArray(this.rows)) return [];
            return this.rows.map((r) => ({
                name: r && r.name != null ? String(r.name) : "",
                number: r && r.number != null ? String(r.number) : "",
                size: r && r.size != null ? String(r.size) : ""
            }));
        },
        hasName() {
            return this.normalizedRows.some((r) => String(r.name || "").trim() !== "");
        },
        hasNumber() {
            return this.normalizedRows.some((r) => String(r.number || "").trim() !== "");
        },
        csvColumns() {
            const defaultColumns = [
                { label: "Name", key: "name" },
                { label: "Number", key: "number" },
                { label: "Size", key: "size" }
            ];
            const normalizedHeader = String(this.csvHeader || "")
                .replace(/\\r\\n/g, "\n")
                .replace(/\\n/g, "\n");
            const firstLine = normalizedHeader
                .split("\n")[0]
                .trim();
            if (!firstLine) return defaultColumns;
            const labelToKey = {
                "name": "name",
                "number": "number",
                "size": "size",
                "product name": "productName",
                "product color": "productColor",
                "name font": "nameFont",
                "name size": "nameSize",
                "name color": "nameColor",
                "number font": "numberFont",
                "number size": "numberSize",
                "number color": "numberColor"
            };
            const parsed = firstLine
                .split(",")
                .map((label) => String(label || "").trim())
                .filter((label) => label !== "")
                .map((label) => ({
                    label,
                    key: labelToKey[String(label || "").toLowerCase()] || null
                }))
                .filter((col) => col.key);
            return parsed.length > 0 ? parsed : defaultColumns;
        }
    },
    methods: {
        _parseMaybeJson(raw) {
            if (!raw) return null;
            if (typeof raw !== "string") return raw;
            try {
                return JSON.parse(raw);
            } catch (e) {
                return null;
            }
        },
        _pickFontConfig(rawObj) {
            const obj = this._parseMaybeJson(rawObj);
            if (!obj || typeof obj !== "object") return null;
            if (Array.isArray(obj)) {
                const firstObj = obj.find(
                    (entry) => entry && typeof entry === "object" && !Array.isArray(entry)
                );
                return firstObj || null;
            }
            const locs = ["front", "back", "left", "right"];
            for (const loc of locs) {
                if (obj[loc] && typeof obj[loc] === "object") {
                    return obj[loc];
                }
            }
            return obj;
        },
        getCellValue(row, key) {
            const r = row || {};
            const item = this.itemContext || {};
            const nameFontObj = this._pickFontConfig(item.font_color_sizes_name);
            const numFontObj = this._pickFontConfig(item.font_color_sizes_number);
            const fallbackByKey = {
                name: [r.name],
                number: [r.number],
                size: [r.size, r.sizeLabel],
                productName: [
                    r.productName,
                    r.product_name,
                    r.product_number,
                    (item.MainNewSubProduct && item.MainNewSubProduct.name) ||
                        (item.NewSubProduct && item.NewSubProduct.name) ||
                        item.product_number
                ],
                productColor: [
                    r.productColor,
                    r.product_color,
                    r.t_shirt_color,
                    (item.color && item.color.name) || item.t_shirt_color
                ],
                nameFont: [r.nameFont, nameFontObj && nameFontObj.nameFontFamily],
                nameSize: [r.nameSize, nameFontObj && nameFontObj.nameFontSize],
                nameColor: [r.nameColor, nameFontObj && nameFontObj.nameFontColor],
                numberFont: [r.numberFont, numFontObj && numFontObj.numberFontFamily],
                numberSize: [r.numberSize, numFontObj && numFontObj.numberFontSize],
                numberColor: [r.numberColor, numFontObj && numFontObj.numberFontColor]
            };
            const values = fallbackByKey[key] || [];
            const found = values.find((v) => v != null && String(v) !== "");
            return found != null ? String(found) : "";
        },
        escapeCsvValue(value) {
            const str = value != null ? String(value) : "";
            if (/[",\n]/.test(str)) {
                return `"${str.replace(/"/g, "\"\"")}"`;
            }
            return str;
        },
        downloadCsv() {
            const rows = Array.isArray(this.rows) ? this.rows : [];
            const columns = this.csvColumns || [];
            let csv = columns.map((c) => this.escapeCsvValue(c.label)).join(",") + "\n";
            rows.forEach((row) => {
                const cells = columns.map((col) =>
                    this.escapeCsvValue(this.getCellValue(row, col.key))
                );
                csv += cells.join(",") + "\n";
            });

            const hiddenElement = document.createElement("a");
            hiddenElement.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
            hiddenElement.download = this.csvFileName || "NameAndNumber.csv";
            hiddenElement.click();
        }
    }
};
</script>

<style scoped>
.name-number-table-wrap {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    flex-wrap: wrap;
}

.data-export-btn {
    flex: 0 0 auto;
    order: 2;
}

.name-number-data {
    flex: 0 1 auto;
    order: 1;
}

.export-btn {
    padding: 6px 12px !important;
    font-size: 14px !important;
    line-height: 1.4 !important;
    min-width: unset !important;
    width: auto !important;
}
</style>

